import React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Input, Label, Spacer } from '@zillow/constellation';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { WATER_SEWER } from '../../../../constants';
import OptionSelector from './OptionSelector';
import HeaderToggle from './HeaderToggle';
import MultiFamilyLandlordUtility, { isMultiFamily } from './MultiFamilyLandlordUtility';
import { CenteredHR } from '../../../Widgets/CenteredHR';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function WaterSewer_v2({ section, setSectionValues }: Props): JSX.Element {
  const isMultiFamilyLandlord = isMultiFamily(section);
  const waterSewer_isEnabled = section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY);

  function setTenantResponsibility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA]: '',
      [WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE]: '',
    });
  }

  function setLandlordResponsibility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [WATER_SEWER.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
    });
  }

  function setServiceProvider(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [WATER_SEWER.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(WATER_SEWER.FIELD_NAME.IS_ENABLED),
    });
  }

  return (
    <>
      <HeaderToggle
        header="Water/sewer"
        subheader="Tenant pays in addition to rent"
        switchOn={waterSewer_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {waterSewer_isEnabled && (
        <>
          <CenteredHR />
          <Spacer marginBottom="sm">
            <OptionSelector
              disabled={!waterSewer_isEnabled}
              optionsText="Who will be responsible for water/sewer set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content={
                isMultiFamilyLandlord
                  ? 'Responsible for account set up and will bill tenant separately.'
                  : 'Responsible for account set up and will bill tenant ' + 'a reasonable proportion separately.'
              }
              onOption1Click={setTenantResponsibility}
              option1Selected={tenantSelected}
              onOption2Click={setLandlordResponsibility}
              option2Selected={landlordSelected}
            />
          </Spacer>

          {tenantSelected && (
            <FormField>
              <Label id="service-provider">What service provider?</Label>
              <Input
                aria-labelledby="service-provider"
                data-test="service-provider"
                placeholder="(example: Wonderland Waterworks)"
                value={section.getFieldValue(WATER_SEWER.FIELD_NAME.SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            </FormField>
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <MultiFamilyLandlordUtility
              section={section}
              utilityName="water/sewer"
              percentageFieldName={WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE}
              formulaFieldName={WATER_SEWER.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA}
              setSectionValues={setSectionValues}
            />
          )}
        </>
      )}
    </>
  );
}
